import { byNumericId, idSetSorted } from './id-sort.ts';

export interface LoketTicketApiImport {
  activeTicketGroupId: string;
  ticketNamesById: Record<string, string>;
  addonNamesById: Record<string, string>;
  activeTicketAddonMap: Record<string, string[]>;
  addonCategoryById: Record<string, string>;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isArray(v: unknown): v is unknown[] {
  return Array.isArray(v);
}

export function parseLoketTicketApiResponse(text: string): LoketTicketApiImport | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }

  if (!isPlainObject(parsed)) return null;

  const result = parsed.result;
  if (!isPlainObject(result)) return null;

  const details = result.details;
  if (!isArray(details)) return null;

  const ticketNamesById: Record<string, string> = {};
  const addonNamesById: Record<string, string> = {};
  const activeTicketAddonMap: Record<string, string[]> = {};
  const addonCategoryById: Record<string, string> = {};

  let activeTicketGroupId = '';

  const validDetails = details.filter(isPlainObject);
  if (validDetails.length === 1) {
    const first = validDetails[0];
    const groupId = first.id_group;
    if (groupId !== undefined && groupId !== null) {
      activeTicketGroupId = String(groupId);
    }
  }

  for (const detail of validDetails) {
    const tickets = detail.tickets;
    if (!isArray(tickets)) continue;

    for (const t of tickets) {
      if (!isPlainObject(t)) continue;
      const id = t.id_ticket;
      if (id === undefined || id === null) continue;
      const ticketId = String(id);
      const name = typeof t.ticket_type === 'string' ? t.ticket_type.trim() : '';
      const mode = t.inventory_mode;

      if (mode === 1) {
        if (activeTicketAddonMap[ticketId] === undefined) {
          activeTicketAddonMap[ticketId] = [];
        }
        if (name) {
          ticketNamesById[ticketId] = name;
        }
      } else if (mode === 2) {
        addonNamesById[ticketId] = name || ticketId;
        if (addonCategoryById[ticketId] === undefined) {
          addonCategoryById[ticketId] = '';
        }
      }
    }
  }

  // Sort IDs
  const sortedMap: Record<string, string[]> = {};
  for (const ticketId of Object.keys(activeTicketAddonMap).sort(byNumericId)) {
    sortedMap[ticketId] = idSetSorted(activeTicketAddonMap[ticketId]);
  }

  const sortedCategory: Record<string, string> = {};
  for (const addonId of Object.keys(addonCategoryById).sort(byNumericId)) {
    sortedCategory[addonId] = addonCategoryById[addonId];
  }

  return {
    activeTicketGroupId,
    ticketNamesById,
    addonNamesById,
    activeTicketAddonMap: sortedMap,
    addonCategoryById: sortedCategory,
  };
}
